/* Un movimiento de stock con su evento: primero el evento, después el saldo.
 *
 * Hasta el 21/10 «Movimiento de stock» y «Movimiento entre depósitos» movían
 * el saldo y DESPUÉS guardaban el evento. Cuando el evento fallaba —y fallaba:
 * los contadores de `sectores` estaban atrás de lo que había bajado de
 * CantApp— el saldo quedaba movido sin registro. El bot copiaba ese orden y
 * le pasó cinco veces en la Oficina de Nico.
 *
 * Ahora:
 *   1. se guarda el evento (si falla, no se tocó nada);
 *   2. se hacen los pasos del saldo en orden;
 *   3. si un paso falla, se deshacen los anteriores de atrás para adelante y
 *      se borra el evento.
 * El evento se borra SÓLO si no quedó nada movido. Si un paso no se puede
 * deshacer, o la base no contestó y no se sabe si el cambio entró, el evento
 * queda —es lo único que explica el saldo— y se dice su número.
 *
 * Lo más seguro sería una función de la base que haga todo en una
 * transacción; es un cambio de esquema que aprueba el dueño. Mientras tanto,
 * esto es lo que garantiza que no quede un saldo movido sin evento.
 */

export class ErrorMovimiento extends Error {
  /** motivo: "registro" | "saldo" | "parcial". `evento`: el que quedó. */
  constructor(motivo, causa, evento = null) {
    super(causa?.message || String(causa || motivo));
    this.name = "ErrorMovimiento";
    this.motivo = motivo;
    this.causa = causa;
    this.evento = evento;
  }
}

/* Un error con código de Postgres/PostgREST es una respuesta: el pedido no se
 * aplicó. Sin código («Failed to fetch») el pedido no volvió y no se sabe. */
const huboRespuesta = (error) => Boolean(error && error.code);

/**
 * @param {object} p
 * @param {() => Promise<{id: string}>} p.registrar  guarda el evento
 * @param {{hacer: Function, deshacer: Function}[]} p.pasos  el saldo, en orden
 * @param {(id: string) => Promise<void>} p.borrarEvento
 * @returns {Promise<{id: string}>}
 */
export const moverConEvento = async ({ registrar, pasos, borrarEvento }) => {
  let evento;
  try {
    evento = await registrar();
  } catch (e) {
    throw new ErrorMovimiento("registro", e);
  }

  const hechos = [];
  for (const paso of pasos) {
    try {
      await paso.hacer();
      hechos.push(paso);
    } catch (e) {
      if (!huboRespuesta(e)) throw new ErrorMovimiento("parcial", e, evento.id);
      for (const hecho of [...hechos].reverse()) {
        try {
          await hecho.deshacer();
        } catch (e2) {
          throw new ErrorMovimiento("parcial", e2, evento.id);
        }
      }
      try {
        await borrarEvento(evento.id);
      } catch (e3) {
        throw new ErrorMovimiento("parcial", e3, evento.id);
      }
      throw new ErrorMovimiento("saldo", e);
    }
  }
  return evento;
};

/** Lo que se le dice a la persona. Nunca «No se pudo consultar». */
export const textoDelMovimiento = (error) => {
  const causa = error?.causa?.code === "23505"
    ? "el número del movimiento ya estaba usado"
    : error?.causa?.message || error?.message || "la base no contestó";
  switch (error?.motivo) {
    case "registro":
      return `No se movió nada: no se pudo guardar el registro del movimiento (${causa}). ` +
        "Probá de nuevo; si sigue, avisale a administración.";
    case "saldo":
      return `No se movió nada: no se pudo actualizar el saldo (${causa}) y el registro se deshizo. ` +
        "Probá de nuevo.";
    case "parcial":
      return `El movimiento quedó a medias (${causa}). Quedó el registro ${error.evento}; ` +
        "avisale a administración para que lo corrija.";
    default:
      return `No se movió nada (${causa}).`;
  }
};
